import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { Stack, Typography } from '@mui/material'
import CopyToOtherStoreys from '@editor/pages/StructuralPlanning/components/MultiEdit/components/CopyToOtherStoreys'
import MultiDelete from '@editor/pages/StructuralPlanning/components/MultiEdit/components/MultiDelete'
import WallPlacement from '@editor/pages/StructuralPlanning/components/MultiEdit/components/WallPlacement'
import WallAlignmentConfirmDialog from '@editor/pages/StructuralPlanning/components/WallAlignmentConfirmDialog'
import { useMutation } from '@tanstack/react-query'
import { CorneredSection } from '@ui2/structure'
import { AlignmentButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { buildErrorMessage } from 'src/constants'
import { alignSelectedWalls } from 'src/state/mutations/alignWalls'
import SelectionInfoRow from '../SelectionInfoRow'

/**
 * Importer-specific multi-edit for walls selected via shift-click or marquee in the planar
 * selection mode: offers the wall placement, copying to other storeys, and the "an SW zuschneiden"
 * and "löschen" actions — the full Structural Planning multi-edit stays out of the import flow.
 */
const IFCWallMultiEdit = (): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)

  // "In anderes Stockwerk kopieren" needs a single source storey (as in Structural Planning's
  // multi-edit): offered only while every selected wall is in the same storey.
  const walls = useModelStore(state => state.model.walls)
  const copySourceStorey = useMemo(() => {
    const selectedWalls = walls.filter(wall => multiActiveElements.includes(wall.guid))
    if (selectedWalls.length === 0) return undefined
    const storey = selectedWalls[0].storey
    return storey && selectedWalls.every(wall => wall.storey === storey) ? storey : undefined
  }, [walls, multiActiveElements])

  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const resetSelection = () => setMultiActiveElements([])

  const { mutate: alignSelectedWallsMutation, isLoading: isAligningSelectedWalls } = useMutation(
    () => alignSelectedWalls.request(projectId as string, multiActiveElements),
    {
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        enqueueSnackbar('Wände erfolgreich ausgerichtet', { variant: 'success' })
        resetSelection()
      },
      onError: (error: AxiosError) => {
        invalidateModel(projectId as string)
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ausrichten der Wände'), {
          variant: 'error',
        })
        resetSelection()
      },
    },
  )

  return (
    <Stack spacing={2}>
      <CorneredSection title="Auswahl">
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Weitere Wände per Umschalt-Klick oder Rahmenauswahl hinzufügen.
          </Typography>

          <SelectionInfoRow
            count={multiActiveElements.length}
            onClear={resetSelection}
            singularLabel="Wand"
            pluralLabel="Wände"
            dataCy="btn-ifc-wall-multi-edit-deselect"
          />
        </Stack>
      </CorneredSection>

      <CorneredSection title="Platzierung">
        <WallPlacement elementGuids={multiActiveElements} />
      </CorneredSection>

      {copySourceStorey && (
        <CorneredSection title="Kopieren">
          <CopyToOtherStoreys selectedElements={multiActiveElements} storey={copySourceStorey} />
        </CorneredSection>
      )}

      <CorneredSection title="Aktionen">
        <Stack direction="row" spacing={1} alignItems="center">
          <WallAlignmentConfirmDialog onConfirm={() => alignSelectedWallsMutation()}>
            <AlignmentButton
              loading={isAligningSelectedWalls}
              data-cy="btn-ifc-wall-multi-edit-align"
              sx={{ paddingX: 1, paddingY: 0.5 }}
            >
              an SW zuschneiden
            </AlignmentButton>
          </WallAlignmentConfirmDialog>
          <MultiDelete selectedElements={multiActiveElements} resetSelected={resetSelection} />
        </Stack>
      </CorneredSection>
    </Stack>
  )
}

export default IFCWallMultiEdit
